import { deepEqual } from 'fast-equals';
import { Form } from 'formik';
import { FC, Fragment, useMemo } from 'react';
// Components
import { Add, Assignment, Close, Delete, Image, Person, Save } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Fade,
  FormControl,
  Grid,
  ImageList,
  ImageListItem,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import { Card, ImageUpload, SMBSelect } from '../../../components';
import { formatMoney } from '../../../helpers';
import { IDomainUser, IDropdownResponse, ITerritoryDetail } from '../../../models';
import { useSnackbar } from 'notistack';

interface ITerritoryModalForm {
  open: boolean;
  currentTerritory: ITerritoryDetail;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  values: any;
  handleBlur: {
    (e: React.FocusEvent<any, Element>): void;
    <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void;
  };
  errors: any;
  touched: any;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  setActualSalesGoal: React.Dispatch<React.SetStateAction<number>>;
  users: IDomainUser[];
  isLoadingUsers: boolean;
  personnelTypes: IDropdownResponse[];
  isLoadingTypes: boolean;
  initialValues: any;
  onClose: () => void;
  showImage: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentImage: React.Dispatch<React.SetStateAction<string>>;
  imagesFiles: File[];
  setImageFiles: React.Dispatch<React.SetStateAction<File[]>>;
  selectedName: string;
  setSelectedName: React.Dispatch<React.SetStateAction<string>>;
  selectedType: string;
  setSelectedType: React.Dispatch<React.SetStateAction<string>>;
  selectedUserName: string;
  setSelectedUserName: React.Dispatch<React.SetStateAction<string>>;
  dirty: boolean;
  isSubmitting: boolean;
  isValid: boolean;
  resetForm: any;
}

export const TerritoryModalForm: FC<ITerritoryModalForm> = ({
  open,
  currentTerritory,
  handleSubmit,
  values,
  handleBlur,
  errors,
  touched,
  setFieldValue,
  users,
  isLoadingUsers,
  personnelTypes,
  isLoadingTypes,
  setActualSalesGoal,
  initialValues,
  onClose,
  showImage,
  setCurrentImage,
  imagesFiles,
  setImageFiles,
  selectedName,
  setSelectedName,
  selectedType,
  setSelectedType,
  selectedUserName,
  setSelectedUserName,
  dirty,
  isSubmitting,
  isValid,
  resetForm
}) => {
  const { enqueueSnackbar } = useSnackbar();

  // Remove all items that are already in values.personnel
  const userDisplay = useMemo(() => {
    return users
      .filter(user => {
        return !values.personnel.some(person => person.userName === user.accountName);
      })
      .map(p => p.displayName);
  }, [users, values.personnel]);

  const userNameValidation = useMemo(() => {
    return values.personnel.find(
      person => person.userName.toLowerCase() === selectedUserName.toLowerCase()
    )
      ? true
      : false;
  }, [values.personnel, selectedUserName]);

  return (
    <Fade in={open}>
      <Form onSubmit={handleSubmit} autoComplete="none">
        <Box marginBottom="1rem">
          <Typography variant="h5">
            {currentTerritory ? `Edit Territory` : 'Add Territory'}
          </Typography>
        </Box>
        <Card title="Info" icon={<Assignment />}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                autoComplete="nope"
                label="Name"
                name="name"
                value={values.name}
                onBlur={handleBlur}
                size="small"
                onChange={e => setFieldValue('name', e.target.value)}
                error={touched.name && errors && errors.name ? true : false}
                helperText={touched.name && errors && errors.name}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                autoComplete="nope"
                label="Starts Per Week"
                name="startsPerWeek"
                value={values.startsPerWeek}
                onBlur={handleBlur}
                size="small"
                error={touched.startsPerWeek && errors && errors.startsPerWeek ? true : false}
                helperText={touched.startsPerWeek && errors && errors.startsPerWeek}
                onChange={e => {
                  const re = /^[0-9\b]+$/;
                  // if value is not blank, then test the regex
                  if (e.target.value === '' || re.test(e.target.value)) {
                    setFieldValue('startsPerWeek', e.target.value);
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                autoComplete="nope"
                label="Build Delay"
                name="buildDelay"
                value={values.buildDelay}
                onBlur={handleBlur}
                size="small"
                onChange={e => {
                  const re = /^[0-9\b]+$/;
                  // if value is not blank, then test the regex
                  if (e.target.value === '' || re.test(e.target.value)) {
                    setFieldValue('buildDelay', e.target.value);
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                autoComplete="nope"
                label="Annual Sales Goal"
                name="annualSalesGoal"
                size="small"
                value={values.annualSalesGoal}
                onBlur={e => {
                  const re = /^[+-]?(\d*\.)?\d+$/;
                  // Need to strip out all non numeric values from formatted value
                  const value = e.target.value.replace('$', '').replace(',', '');
                  // make sure it is number only before we update state
                  if (value === '' || re.test(value)) {
                    setActualSalesGoal(Number(value));
                  }
                  setFieldValue('annualSalesGoal', formatMoney(e.target.value, 2));
                }}
                onChange={e => {
                  setFieldValue('annualSalesGoal', e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                autoComplete="nope"
                label="Narrative"
                name="description"
                value={values.description}
                onBlur={handleBlur}
                size="small"
                multiline
                minRows={3}
                error={touched.description && errors && errors.description ? true : false}
                helperText={touched.description && errors && errors.description}
                inputProps={{
                  style: {
                    resize: 'vertical'
                  }
                }}
                onChange={e => setFieldValue('description', e.target.value)}
              />
            </Grid>
          </Grid>
        </Card>
        <Card title="Images" icon={<Image />}>
          <>
            <FormControl fullWidth variant="outlined" size="small">
              <ImageUpload
                handleFileChange={(val, file) => {
                  setImageFiles([...(imagesFiles || []), file]);
                  setFieldValue('images', [...(values.images || []), val]);
                }}
                image={null}
                showSource={false}
              />
            </FormControl>
            {values.images && values.images.length > 0 && (
              <ImageList cols={3} gap={7}>
                {values.images.map((image, index) => {
                  return (
                    <ImageListItem key={`${index}`}>
                      <img
                        src={image}
                        alt={`territory ${index}`}
                        onClick={() => {
                          showImage(true);
                          setCurrentImage(image);
                        }}
                      />
                    </ImageListItem>
                  );
                })}
              </ImageList>
            )}
          </>
        </Card>
        <Card title="Personnel" icon={<Person />}>
          <Grid container spacing={1} alignItems="flex-start">
            <Grid item xs={12} sm={6} md={4} alignItems={'flex-start'}>
              <FormControl fullWidth variant="outlined" size="small">
                <Autocomplete
                  value={selectedName}
                  onChange={(event, newValue: string) => {
                    const selected = users.find(u => u.displayName === newValue);
                    if (selected) {
                      setSelectedUserName(selected.accountName);
                    }
                    if (newValue && newValue.length > 0) {
                      setSelectedName(newValue);
                    } else {
                      setSelectedName('');
                    }
                  }}
                  id="name"
                  options={userDisplay ?? []}
                  disabled={isLoadingUsers}
                  getOptionLabel={(option: string) => {
                    return option;
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      size="small"
                      name="name"
                      autoComplete="none"
                      label="Name"
                      variant="outlined"
                      error={userNameValidation}
                      helperText={userNameValidation && 'User is already assigned to the territory'}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SMBSelect
                name="type"
                label="Type"
                id="type"
                variant="outlined"
                handleClear={() => setSelectedType('')}
                disabled={isLoadingTypes}
                value={selectedType}
                error={userNameValidation}
                onChange={({ target: { value } }) => {
                  setSelectedType(value as string);
                }}
              >
                {personnelTypes &&
                  personnelTypes.map((type, index) => {
                    return (
                      <MenuItem key={`${index}`} value={type.description}>
                        {type.description}
                      </MenuItem>
                    );
                  })}
              </SMBSelect>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                disabled={!selectedType || !selectedName || userNameValidation}
                type="button"
                startIcon={<Add />}
                variant="contained"
                color="primary"
                onClick={() => {
                  setFieldValue('personnel', [
                    ...values.personnel,
                    {
                      personnelType: selectedType,
                      userName: selectedUserName,
                      isActive: true
                    }
                  ]);
                  setSelectedName('');
                  setSelectedType('');
                  setSelectedUserName('');
                }}
              >
                Add User
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <List dense>
                {values.personnel
                  //Sort Alphabetically
                  .sort((a, b) => a.userName.localeCompare(b.userName))
                  .map((p, index) => {
                    return (
                      <Fragment key={`${index}`}>
                        <ListItem
                          secondaryAction={
                            <Button
                              startIcon={<Delete />}
                              color="error"
                              aria-label="delete"
                              onClick={() => {
                                const result = window.confirm(
                                  'Are you sure you want to remove this personnel?'
                                );
                                if (result) {
                                  setFieldValue(
                                    'personnel',
                                    values.personnel.filter(
                                      person => person.userName !== p.userName
                                    )
                                  );
                                  enqueueSnackbar(`${p.userName} removed successfully!`, {
                                    variant: 'success'
                                  });
                                }
                              }}
                            >
                              Delete
                            </Button>
                          }
                        >
                          <ListItemText
                            primary={p.userName}
                            secondary={`${p.personnelType} - ${p.isActive ? 'Active' : 'Inactive'}`}
                          />
                        </ListItem>
                        {index !== values.personnel.length - 1 && <Divider />}
                      </Fragment>
                    );
                  })}
              </List>
            </Grid>
          </Grid>
        </Card>
        <Box marginTop="2rem">
          <Button
            disabled={!dirty || isSubmitting || !isValid}
            type="submit"
            startIcon={<Save />}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
          <Button
            type="button"
            variant="contained"
            startIcon={<Close />}
            color="inherit"
            onClick={() => {
              if (!deepEqual(initialValues, values)) {
                const result = window.confirm(
                  'You have unsaved changes, are you sure you want to exit?'
                );
                if (result) {
                  // reset state
                  setImageFiles(null);
                  resetForm();
                  onClose();
                } else {
                  return;
                }
              } else {
                onClose();
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </Form>
    </Fade>
  );
};
